import { memo, useCallback, useMemo } from 'react'
import { FaThumbsUp } from "react-icons/fa";
import { formatCommentDate } from '../../../Utils/formatDateTwo';

const CommentItem = ({
	comment,
	breakLongWords,
	handleLike,
	likingCommentIds,
	optimisticLikes,
	setReplyingTo,
	optimisticLikeCounts
}) => {
	const onLike = useCallback(() => {
		handleLike(comment._id);
	}, [handleLike, comment._id]);

	const onReply = useCallback(() => {
		setReplyingTo(comment);
	}, [setReplyingTo, comment]);

	const avatarSrc = useMemo(() => {
		return comment.user?.profileImage ||
			`https://api.dicebear.com/7.x/avataaars/svg?seed=${comment.user?._id || comment._id}`;
	}, [comment.user?.profileImage, comment.user?._id, comment._id]);

	const liked =
		optimisticLikes[comment._id] ?? comment.isLiked;

	const likeCount =
		optimisticLikeCounts[comment._id] ?? comment.likeCount;

	return (
		<div key={comment._id} className="group animate-in fade-in slide-in-from-bottom-2 duration-300">
			{/* Parent Comment */}
			<div className="flex items-start gap-3">
				<img
					src={avatarSrc}
					alt="avatar"
					className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-stone-100 object-cover ring-2 ring-white shadow-sm"
				/>
				<div className="flex-1 min-w-0">
					<div className="bg-stone-50 rounded-2xl rounded-tl-none px-4 py-3 border border-stone-200/50">
						<div className="flex items-baseline justify-between mb-1">
							<span className="text-sm font-bold text-stone-800 truncate">
								{comment.user?.fullName || "User"}
							</span>
							<span className="text-[10px] uppercase tracking-wider font-bold text-stone-400">
								{formatCommentDate(comment.createdAt)}
							</span>
						</div>
						<p className="text-[13px] sm:text-sm text-stone-600 leading-relaxed break-words whitespace-pre-line">
							{breakLongWords(comment.text)}
						</p>
					</div>

					{/* Actions */}
					<div className="flex items-center gap-4 mt-2 ml-1">
						<button
							onClick={onLike}
							disabled={comment._id.startsWith("temp-") || likingCommentIds.has(comment._id)}
							className={`flex items-center gap-1.5 text-xs font-bold transition-all hover:scale-105 ${liked ? "text-amber-600" : "text-stone-500 hover:text-stone-800"
								}`}
						>
							<FaThumbsUp className={liked ? "text-amber-600" : ""} size={12} />
							<span>{likeCount}</span>
						</button>

						{!comment.isOptimistic && (
							<button
								onClick={onReply}
								className="text-xs font-bold text-stone-500 hover:text-stone-800 transition-colors"
							>
								Reply
							</button>
						)}
					</div>

					{/* Nested Replies */}
					{comment.replies && comment.replies.length > 0 && (
						<div className="mt-4 space-y-4 pl-4 border-l-2 border-stone-200">
							{comment.replies.map((reply) => (
								<div key={reply._id} className="flex items-start gap-2">
									<img
										src={reply.user?.profileImage || `https://api.dicebear.com/7.x/avataaars/svg?seed=${reply.user?._id}`}
										alt=""
										className="w-7 h-7 rounded-lg bg-stone-100 object-cover ring-1 ring-stone-200"
									/>
									<div className="flex-1 bg-white border border-stone-200 rounded-xl px-3 py-2 shadow-sm">
										<div className="flex items-center justify-between gap-2">
											<span className="text-xs font-bold text-stone-700 truncate">{reply.user?.fullName}</span>
											<span className="text-[9px] font-medium text-stone-400 uppercase">{formatCommentDate(reply.createdAt)}</span>
										</div>
										<p className="text-xs text-stone-600 mt-1">{breakLongWords(reply.text)}</p>
									</div>
								</div>
							))}
						</div>
					)}
				</div>
			</div>
		</div>
	)
}

export default memo(CommentItem);